/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  Input,
  ElementRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { TabsModule } from "primeng/tabs";
import { BadgeModule } from "primeng/badge";
import { LegacyDashboardComponent } from "../../../sites/components/legacy-dashboard/legacy-dashboard.component";
import { RevenueDashboardComponent } from "../revenue-dashboard/revenue-dashboard.component";
import { DashboardContainerSkeletonComponent } from "../dashboard-container-skeleton/dashboard-container-skeleton.component";
import { AnalyticsSetupRequiredComponent } from "../analytics-setup-required/analytics-setup-required.component";
import {
  QPilotService,
  QPilotAccessToken,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { AnalyticsStatusResponse } from "../../interfaces/analytics-status.interface";

interface TabConfig {
  value: number;
  label: string;
  component: "dashboard" | "revenue" | "scheduled-orders" | "errors";
  badge?: { label: string; severity: "success" | "warn" };
  disabled?: boolean;
}

@Component({
  selector: "app-dashboard-container",
  imports: [
    CommonModule,
    TabsModule,
    BadgeModule,
    LegacyDashboardComponent,
    RevenueDashboardComponent,
    DashboardContainerSkeletonComponent,
    AnalyticsSetupRequiredComponent,
  ],
  templateUrl: "./dashboard-container.component.html",
  styleUrl: "./dashboard-container.component.scss",
})
export class DashboardContainerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Input properties for embedded mode (web component / iframe)
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;

  siteId: string | null = null;
  activeTabIndex = 0;
  analyticsStatus = signal<AnalyticsStatusResponse | null>(null);
  isShopifySite = signal<boolean>(false);
  tabs = signal<TabConfig[]>([]);
  isLoading = signal<boolean>(true);

  constructor(
    private route: ActivatedRoute,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check for embedded mode from HTML attribute (web component mode)
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from input/attribute (for embedded mode)
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    // Get token from attribute and convert (for embedded mode)
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    // Create inputToken from attribute if:
    // 1. We have a token string from attribute
    // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
    // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Initialize based on mode
    if (this.embeddedMode && siteIdString) {
      this.siteId = siteIdString;
      this.isLoading.set(true);
      this.loadSiteData();
      this.loadAnalyticsStatus();
    } else {
      // Standard mode: get siteId from route params
      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.siteId = params.get("siteId");
        if (this.siteId) {
          this.isLoading.set(true);
          this.loadSiteData();
          this.loadAnalyticsStatus();
        }
      });
    }
  }

  private loadSiteData(): void {
    if (!this.siteId) return;

    const siteId = parseInt(this.siteId, 10);
    const accessToken = this.getAccessToken();

    if (!accessToken) return;

    this.qpilotService
      .getSite(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site) => {
          // Check if this is a Shopify site (matches scheduled-orders pattern)
          this.isShopifySite.set((site as any).Integration?.Name === "Shopify");
          // Rebuild tabs with updated platform info
          const analyticsEnabled =
            this.analyticsStatus()?.isAnalyticsEnabled ?? false;
          this.buildTabs(analyticsEnabled);
        },
        error: (error) => {
          console.error("Error fetching site data:", error);
          // Default to non-Shopify if fetch fails
          this.isShopifySite.set(false);
        },
      });
  }

  private loadAnalyticsStatus(): void {
    if (!this.siteId) return;

    const siteId = parseInt(this.siteId, 10);
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      // If no access token, default to analytics disabled
      this.analyticsStatus.set({ isAnalyticsEnabled: false });
      this.buildTabs(false);
      return;
    }

    this.qpilotService
      .getAnalyticsStatus(siteId, accessToken, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          this.analyticsStatus.set(status);
          this.buildTabs(status.isAnalyticsEnabled);
        },
        error: () => {
          // If endpoint fails, default to analytics disabled
          this.analyticsStatus.set({ isAnalyticsEnabled: false });
          this.buildTabs(false);
        },
      });
  }

  /**
   * Get access token based on current mode
   * In embedded mode, use inputToken; in standard mode, use sessionService
   */
  private getAccessToken(): QPilotAccessToken | null {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken;
  }

  private buildTabs(analyticsEnabled: boolean): void {
    const isShopify = this.isShopifySite();

    const dashboardTab: TabConfig = {
      value: analyticsEnabled ? 1 : 0,
      label: "Dashboard",
      component: "dashboard",
    };

    // Conditional badge based on platform
    const revenueBadge: { label: string; severity: "success" | "warn" } =
      isShopify
        ? { label: "Coming Soon", severity: "warn" }
        : { label: "New!", severity: "success" };

    const revenueTab: TabConfig = {
      value: analyticsEnabled ? 0 : 1,
      label: "Revenue",
      component: "revenue",
      badge: revenueBadge,
    };

    const scheduledOrdersTab: TabConfig = {
      value: 2,
      label: "Scheduled Orders",
      component: "scheduled-orders",
      badge: { label: "Coming Soon", severity: "warn" },
      disabled: true,
    };

    const errorsTab: TabConfig = {
      value: 3,
      label: "Errors",
      component: "errors",
      badge: { label: "Coming Soon", severity: "warn" },
      disabled: true,
    };

    // Order tabs based on analytics status
    const orderedTabs = analyticsEnabled
      ? [revenueTab, dashboardTab, scheduledOrdersTab, errorsTab]
      : [dashboardTab, revenueTab, scheduledOrdersTab, errorsTab];

    this.tabs.set(orderedTabs);

    // Stop loading once tabs are built
    this.isLoading.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
